import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Storage } from './storage';
import Modal from './components/Modal';
import Icon from './components/Icon';
import OnboardingModal from './components/OnboardingModal';
import AuthScreen from './pages/AuthScreen';
import AuthPromptModal from './components/AuthPromptModal';
import Dashboard from './pages/Dashboard';
import TimelineEditor from './pages/TimelineEditor';
import TimelineViewer from './pages/TimelineViewer';
import Feed from './pages/Feed';
import ProfilePage from './pages/ProfilePage';
import PublicProfile from './pages/PublicProfile';
import AdminPanel from './pages/AdminPanel';
import TimelineEmbed from './pages/TimelineEmbed';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CollectionPage from './pages/CollectionPage';
import { useConsent } from './contexts/ConsentContext';

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
//
//  Top-level component. Owns global state (user, timelines, current view) and
//  acts as a lightweight client-side router between five screens:
//    auth       → AuthScreen
//    dashboard  → Dashboard
//    editor     → TimelineEditor
//    viewer     → TimelineViewer
//    profile    → ProfilePage
//

const App = () => {
  const { consent } = useConsent();

  // Dynamically enable Sentry session replay when analytics consent is granted.
  // Uses a ref so we only initialize replay once per session.
  const replayInitRef = useRef(false);
  useEffect(() => {
    if (consent.analytics && !replayInitRef.current && import.meta.env.VITE_SENTRY_DSN) {
      replayInitRef.current = true;
      import('@sentry/react').then(S => {
        S.addIntegration(S.replayIntegration({
          maskAllText:    true,
          blockAllMedia:  false,
        }));
      });
    }
  }, [consent.analytics]);

  const [user,             setUser]             = useState(null);
  const [timelines,        setTimelines]        = useState([]);
  const [sharedTimelines,  setSharedTimelines]  = useState([]);
  const [view,             setView]             = useState('feed');
  const [activeTimeline,   setActiveTimeline]   = useState(null);
  const [collaboratorRole, setCollaboratorRole] = useState(null); // null = owner, 'editor' | 'viewer' for shared
  const [fromTemplateName, setFromTemplateName] = useState(null); // set when editor opened from template
  const [feedViewAuthor,   setFeedViewAuthor]   = useState(null);
  const [profileViewUser,  setProfileViewUser]  = useState(null);
  const [viewerReturn,     setViewerReturn]     = useState('dashboard'); // 'dashboard'|'feed'|'publicProfile'
  const [initialDevBlogPostId, setInitialDevBlogPostId] = useState(() => {
    const m = window.location.pathname.match(/^\/dev-blog\/([a-z0-9]+)$/i);
    if (m) return m[1];
    if (window.location.pathname === '/dev-blog') return true;
    return null;
  });
  const [theme,            setTheme]            = useState(() => Storage.getTheme());
  const [pendingView,           setPendingView]           = useState(null);
  const [gatePassword,          setGatePassword]          = useState('');
  const [gateError,             setGateError]             = useState('');
  const [gateLoading,           setGateLoading]           = useState(false);
  const [initialising,          setInitialising]          = useState(true);
  const [sessionExpiresAt,      setSessionExpiresAt]      = useState(null);
  const [showExpiryBanner,      setShowExpiryBanner]      = useState(false);
  const [expiryBannerDismissed, setExpiryBannerDismissed] = useState(false);
  const [sessionExpiredNotice,  setSessionExpiredNotice]  = useState(false);
  const [uploadWarning,         setUploadWarning]         = useState(null);
  const [swUpdateAvailable,     setSwUpdateAvailable]     = useState(false);
  const [installPromptEvent,    setInstallPromptEvent]    = useState(null);
  const swRegistrationRef = useRef(null);

  // ── Guest / unverified-user state ─────────────────────────────────────────
  // guestMode: true when a non-authenticated user is browsing public content.
  // pendingVerifyEmail: set when guest mode was entered because login was
  //   blocked due to unverified email — drives a specific "check inbox" banner.
  const [guestMode,          setGuestMode]          = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
  // authPrompt: non-null when we want to show the "create account to do X" modal.
  const [authPrompt,         setAuthPrompt]         = useState(null); // { action: string }

  // ── PWA: service worker update detection ─────────────────────────────────
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegistered(r) { swRegistrationRef.current = r; },
    onNeedRefresh()  { setSwUpdateAvailable(true); },
  });

  // ── PWA: capture beforeinstallprompt for deferred install ────────────────
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPromptEvent(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const isStandalone = typeof window !== 'undefined' &&
    window.matchMedia('(display-mode: standalone)').matches;

  const triggerInstall = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    if (outcome === 'accepted') setInstallPromptEvent(null);
  };
  const [showOnboarding,        setShowOnboarding]        = useState(false);
  const [pendingOpenCreate,     setPendingOpenCreate]     = useState(false);
  const [showFirstTimeline,     setShowFirstTimeline]     = useState(false);
  const [firstTimelineId,       setFirstTimelineId]       = useState(null);
  const [pendingCsvImport,      setPendingCsvImport]      = useState(false);

  // ── Invite deep-link state ────────────────────────────────────────────────
  const [inviteToken,     setInviteToken]     = useState(null);
  const [inviteInfo,      setInviteInfo]      = useState(null);  // { timelineTitle, inviterName, role, email, expiresAt }
  const [inviteLoading,   setInviteLoading]   = useState(false);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const [inviteDeclining, setInviteDeclining] = useState(false);
  const [inviteError,     setInviteError]     = useState('');
  const [inviteAccepted,  setInviteAccepted]  = useState(false);

  // ── Ownership transfer deep-link state ────────────────────────────────────
  const [transferToken,      setTransferToken]      = useState(null);
  const [transferAction,     setTransferAction]     = useState(null); // 'accept' | 'decline'
  const [transferProcessing, setTransferProcessing] = useState(false);
  const [transferResult,     setTransferResult]     = useState(null); // { ok, action, timelineId?, timelineTitle? } | { ok: false, error }

  // ── Apply theme to <html> ─────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // ── Restore session on mount ──────────────────────────────────────────────
  //
  //  Validates the stored token against the API.  If valid, also loads the
  //  user's timelines before revealing the app so there is no empty-dashboard
  //  flash on refresh.
  useEffect(() => {
    // Extract invite token from URL before clearing it — works whether the user
    // is already signed in or needs to log in first.
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('invite_token');
    if (token) {
      window.history.replaceState({}, '', '/');
      setInviteToken(token);
      setInviteLoading(true);
      Storage.getInvite(token)
        .then(info => { setInviteInfo(info); setInviteLoading(false); })
        .catch(() => { setInviteError('This invite link is invalid or has expired.'); setInviteLoading(false); });
    }

    const acceptToken  = params.get('transfer_accept');
    const declineToken = params.get('transfer_decline');
    if (acceptToken) {
      window.history.replaceState({}, '', '/');
      setTransferToken(acceptToken);
      setTransferAction('accept');
    } else if (declineToken) {
      window.history.replaceState({}, '', '/');
      setTransferToken(declineToken);
      setTransferAction('decline');
    }

    Storage.getSession().then(async (sessionData) => {
      if (sessionData) {
        const { user: u, expiresAt } = sessionData;
        const [tls, shared] = await Promise.all([
          Storage.getTimelines(u.id).catch(() => []),
          Storage.getSharedTimelines().catch(() => []),
          Storage.fetchCsrfToken(), // fetch CSRF token for cookie-based auth
        ]);
        setTimelines(tls);
        setSharedTimelines(shared);
        setUser(u);
        setSessionExpiresAt(expiresAt);
        if (!u.hasOnboarded) setShowOnboarding(true);
        if (token) setView('dashboard');
      }
      setInitialising(false);

      // Handle deep links: /timeline/:id  — load and navigate to that timeline
      const match = window.location.pathname.match(/^\/timeline\/([a-z0-9]+)$/i);
      if (match) {
        window.history.replaceState({}, '', '/');
        Storage.getPublicTimeline(match[1])
          .then(({ tl, author }) => {
            setFeedViewAuthor(author);
            setViewerReturn('feed');
            setActiveTimeline(tl);
            setView('viewer');
            // Auto-enable guest mode for unauthenticated visitors so the
            // shared link goes straight to the timeline instead of the
            // sign-up screen.  Anyone with the link should be able to
            // view a public timeline without an account.
            if (!sessionData) setGuestMode(true);
          })
          .catch((err) => {
            if (err.status === 403 || err.requiresPassword) {
              setPendingView({
                id:        err.tl?.id       ?? match[1],
                title:     err.tl?.title    ?? 'Private Timeline',
                privacy:   err.tl?.privacy  ?? 'private',
                isPrivate: !!err.isPrivate,
              });
              // Even for password-protected, drop into guest mode so the
              // gate modal renders over the feed instead of the AuthScreen.
              if (!sessionData) setGuestMode(true);
            }
          });
      }
    });
  }, []);

  // ── Session expiry event (fired by storage._fetch on SESSION_EXPIRED 401) ──

  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      setTimelines([]);
      setSharedTimelines([]);
      setCollaboratorRole(null);
      setSessionExpiresAt(null);
      setShowExpiryBanner(false);
      setView('feed');
      setSessionExpiredNotice(true);
    };
    window.addEventListener('chronos:session-expired', handleExpired);
    return () => window.removeEventListener('chronos:session-expired', handleExpired);
  }, []);

  useEffect(() => {
    const handleUploadWarning = (e) => {
      setUploadWarning(e.detail);
      setTimeout(() => setUploadWarning(null), 5000);
    };
    window.addEventListener('chronos:upload-warning', handleUploadWarning);
    return () => window.removeEventListener('chronos:upload-warning', handleUploadWarning);
  }, []);

  // ── Session expiry warning banner (show if < 48 h remaining) ─────────────

  useEffect(() => {
    if (!sessionExpiresAt || expiryBannerDismissed) return;
    const hoursLeft = (new Date(sessionExpiresAt) - Date.now()) / 36e5;
    if (hoursLeft > 0 && hoursLeft < 48) setShowExpiryBanner(true);
  }, [sessionExpiresAt, expiryBannerDismissed]);

  // ── Page-visibility re-validation ────────────────────────────────────────
  //
  //  When the user returns to the tab after a long absence, silently check
  //  whether the session is still valid.  If it expired while they were away,
  //  surface the auth screen immediately rather than letting them hit a
  //  confusing 401 on their next save or comment.

  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible' || !user) return;
      const sessionData = await Storage.getSession().catch(() => null);
      if (!sessionData) {
        setUser(null);
        setTimelines([]);
        setSharedTimelines([]);
        setCollaboratorRole(null);
        setSessionExpiresAt(null);
        setView('feed');
        setSessionExpiredNotice(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user]);

  // ── Auth handlers ─────────────────────────────────────────────────────────

  const handleLogin = async (u) => {
    const [tls, shared] = await Promise.all([
      Storage.getTimelines(u.id).catch(() => []),
      Storage.getSharedTimelines().catch(() => []),
      Storage.fetchCsrfToken(),
    ]);
    setTimelines(tls);
    setSharedTimelines(shared);
    setUser(u);
    setGuestMode(false);
    setPendingVerifyEmail(null);
    setAuthPrompt(null);
    Sentry.setUser({ id: String(u.id), username: u.name });
    if (!u.hasOnboarded) setShowOnboarding(true);
    // If a token was captured before login, switch to dashboard so the modal renders.
    if (inviteToken || (transferToken && transferAction === 'accept')) setView('dashboard');
  };

  const handleLogout = async () => {
    await Storage.clearSession();
    Sentry.setUser(null);
    setUser(null);
    setTimelines([]);
    setSharedTimelines([]);
    setCollaboratorRole(null);
    setGuestMode(false);
    setPendingVerifyEmail(null);
    setAuthPrompt(null);
    setView('feed');
  };

  // Enter browse-as-guest mode.  email is set when an unverified user was
  // redirected here after a failed login so we can show the right banner.
  const handleGuestEntry = (email = null) => {
    setGuestMode(true);
    setPendingVerifyEmail(email || null);
    setAuthPrompt(null);
    setView('feed');
  };

  // Called by child components when a guest tries a gated action.
  const handleAuthRequired = (action) => {
    setAuthPrompt({ action });
  };

  // Leave guest mode and return to the auth screen (sign in or sign up).
  const handleLeaveGuest = () => {
    setAuthPrompt(null);
    setGuestMode(false);
    setPendingVerifyEmail(null);
  };

  // ── Timeline CRUD handlers ────────────────────────────────────────────────

  const handleCreate = async (tl, alreadyCreated = false, opts = {}) => {
    let isFirst = false;
    if (!alreadyCreated) {
      try {
        const { isFirst: first } = await Storage.createTimeline(tl);
        isFirst = !!first;
      } catch (err) {
        alert(`Failed to create timeline: ${err.message}`);
        return;
      }
    } else {
      // Timeline was already created server-side (from-template path).
      // Show the template guidance banner in the editor.
      setFromTemplateName(tl.title || 'template');
    }
    setTimelines(prev => [...prev, tl]);
    setActiveTimeline(tl);
    if (isFirst) {
      setFirstTimelineId(tl.id);
      setShowFirstTimeline(true);
    }
    if (opts.openCsvImport) setPendingCsvImport(true);
    setView('editor');
  };

  const handleOnboardingComplete = () => {
    Storage.markOnboarded(user.id).catch(() => {});
    setShowOnboarding(false);
    setPendingOpenCreate(true);
    setView('dashboard');
  };

  const handleOnboardingExplore = () => {
    Storage.markOnboarded(user.id).catch(() => {});
    setShowOnboarding(false);
    setView('feed');
  };

  const handleSave = async (tl) => {
    try {
      const updated = await Storage.updateTimeline(tl);
      setTimelines(prev => prev.map(t => t.id === updated.id ? updated : t));
      // Also propagate to shared list so collaborator's view stays fresh.
      setSharedTimelines(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
      setActiveTimeline(updated);
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    // Optimistic update: remove from state immediately.
    setTimelines(prev => prev.filter(t => t.id !== id));
    Storage.deleteTimeline(id).catch(err =>
      console.error('Failed to delete timeline:', err.message)
    );
  };

  const handleDuplicate = async (id, newTitle) => {
    const newTl = await Storage.duplicateTimeline(id, newTitle);
    setTimelines(prev => [newTl, ...prev]);
    return newTl.id;
  };

  const handleNotificationClick = (timelineId) => {
    const owned  = timelines.find(t => t.id === timelineId);
    const shared = sharedTimelines.find(t => t.id === timelineId);
    const local  = owned || shared;
    if (local) {
      setFeedViewAuthor(null);
      setViewerReturn('dashboard');
      setActiveTimeline(local);
      setView('viewer');
    } else {
      Storage.getPublicTimeline(timelineId)
        .then(({ tl, author }) => {
          setFeedViewAuthor(author ?? null);
          setViewerReturn('dashboard');
          setActiveTimeline(tl);
          setView('viewer');
        })
        .catch(() => {});
    }
  };

  // ── Theme handler ─────────────────────────────────────────────────────────

  const handleThemeToggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    Storage.saveTheme(next);
  };

  // ── Profile handler ───────────────────────────────────────────────────────

  const handleProfileSave = async (updatedUser) => {
    const saved = await Storage.updateUser(updatedUser);
    setUser(saved);
    setView('dashboard');
  };

  const handleDeleteAccount = () => {
    setUser(null);
    setTimelines([]);
    setSharedTimelines([]);
    setCollaboratorRole(null);
    setView('feed');
  };

  // ── Navigation ────────────────────────────────────────────────────────────

  const handleView = (tl) => {
    if (tl.privacy === 'private' && tl.hasPassword) {
      setPendingView(tl);
    } else {
      setFeedViewAuthor(null);
      setViewerReturn('dashboard');
      setActiveTimeline(tl);
      setView('viewer');
    }
  };

  const handleEdit = (tl) => {
    // tl.collaboratorRole is set on shared timeline objects; null/undefined for owned ones.
    setCollaboratorRole(tl.collaboratorRole || null);
    setActiveTimeline(tl);
    setView('editor');
  };

  // Called from the Feed when a user clicks View on any public timeline
  const handleFeedView = (tl, author) => {
    setFeedViewAuthor(author);
    setViewerReturn('feed');
    if (tl.privacy === 'password-protected') {
      setPendingView(tl);
    } else {
      setActiveTimeline(tl);
      setView('viewer');
    }
  };

  // Navigate to a public user profile — accepts a user object or a plain ID string
  const handleViewProfile = (authorOrId) => {
    const id = typeof authorOrId === 'string' ? authorOrId : authorOrId?.id;
    if (!id) return;
    setProfileViewUser(typeof authorOrId === 'string' ? { id } : authorOrId);
    setView('publicProfile');
  };

  // Called from PublicProfile when viewing one of their timelines
  const handleProfileViewTimeline = (tl, author) => {
    setFeedViewAuthor(author);
    setViewerReturn('publicProfile');
    setActiveTimeline(tl);
    setView('viewer');
  };

  const closeGate = () => { setPendingView(null); setGatePassword(''); setGateError(''); setGateLoading(false); };

  const handleGate = async () => {
    setGateError('');
    setGateLoading(true);
    try {
      const { tl, author } = await Storage.getPublicTimeline(pendingView.id, gatePassword);
      Storage.storeTimelinePassword(pendingView.id, gatePassword);
      setFeedViewAuthor(author ?? feedViewAuthor);
      setViewerReturn(viewerReturn === 'publicProfile' ? 'publicProfile' : 'feed');
      setActiveTimeline(tl);
      setView('viewer');
      closeGate();
    } catch {
      setGateError('Incorrect password.');
      setGateLoading(false);
    }
  };

  // ── Invite accept / decline ───────────────────────────────────────────────

  const handleAcceptInvite = async () => {
    setInviteAccepting(true);
    setInviteError('');
    try {
      await Storage.acceptInvite(inviteToken);
      const shared = await Storage.getSharedTimelines().catch(() => []);
      setSharedTimelines(shared);
      setInviteAccepted(true);
      setInviteToken(null);
      setInviteInfo(null);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteAccepting(false);
    }
  };

  const handleDeclineInvite = async () => {
    setInviteDeclining(true);
    try { await Storage.declineInvite(inviteToken); } catch {}
    setInviteToken(null);
    setInviteInfo(null);
    setInviteError('');
    setInviteDeclining(false);
  };

  const dismissInviteAccepted = () => {
    setInviteAccepted(false);
    setView('dashboard');
  };

  // ── Ownership transfer handlers ───────────────────────────────────────────

  const processTransfer = async (token, action) => {
    setTransferProcessing(true);
    try {
      if (action === 'accept') {
        const result = await Storage.acceptTransfer(token);
        setTransferResult({ ok: true, action: 'accepted', timelineId: result.timelineId, timelineTitle: result.timelineTitle });
        // Refresh timelines list so the transferred timeline appears
        if (user) {
          const [tls, shared] = await Promise.all([
            Storage.getTimelines(user.id).catch(() => []),
            Storage.getSharedTimelines().catch(() => []),
          ]);
          setTimelines(tls);
          setSharedTimelines(shared);
        }
      } else {
        await Storage.declineTransfer(token);
        setTransferResult({ ok: true, action: 'declined' });
      }
    } catch (err) {
      setTransferResult({ ok: false, error: err.message || 'This transfer link is invalid or has expired.' });
    } finally {
      setTransferProcessing(false);
      setTransferToken(null);
      setTransferAction(null);
    }
  };

  // Auto-process transfer tokens once auth state is known
  useEffect(() => {
    if (!transferToken || !transferAction) return;
    if (transferProcessing || transferResult) return;
    if (transferAction === 'decline') {
      processTransfer(transferToken, 'decline');
    } else if (transferAction === 'accept' && user) {
      processTransfer(transferToken, 'accept');
    }
    // If accept + no user: wait — will fire again when user logs in
  }, [transferToken, transferAction, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guest navigation guard ────────────────────────────────────────────────
  //
  //  Guests can only access feed, viewer, and publicProfile.  If something
  //  pushes them toward dashboard/editor/profile/admin (e.g. a deep-link or
  //  internal navigation), redirect back to feed and optionally surface the
  //  auth prompt so they understand why.

  useEffect(() => {
    if (user || !guestMode) return;
    const authOnly = ['dashboard', 'editor', 'profile', 'admin'];
    if (authOnly.includes(view)) {
      setView('feed');
      if (view === 'dashboard' || view === 'editor') {
        setAuthPrompt({ action: 'create' });
      }
    }
  }, [user, guestMode, view]);

  // ── Render ────────────────────────────────────────────────────────────────

  // Embed route: /embed/:id — render without auth, no chrome
  const embedMatch = window.location.pathname.match(/^\/embed\/([a-z0-9_-]+)$/i);
  if (embedMatch) return <TimelineEmbed timelineId={embedMatch[1]} />;

  // Public pages — accessible without auth
  if (window.location.pathname === '/terms')   return <TermsOfService />;
  if (window.location.pathname === '/privacy') return <PrivacyPolicy />;

  // Blank loading screen while the session token is being validated against
  // the API — prevents a flash of the sign-in page on hard refresh.
  if (initialising) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text3)', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  // Collection route: /collections/:id — public; accessible without auth
  const collectionMatch = window.location.pathname.match(/^\/collections\/([a-z0-9]+)$/i);
  if (collectionMatch) {
    return <CollectionPage collectionId={collectionMatch[1]} currentUser={user} />;
  }

  if (!user && !guestMode) {
    return (
      <>
        <AuthScreen
          onLogin={handleLogin}
          onGuestEntry={handleGuestEntry}
          inviteInfo={inviteInfo}
          inviteLoading={inviteLoading}
          expiredNotice={sessionExpiredNotice}
        />
        {/* Invite error when token is bad and user is not logged in */}
        {inviteError && !inviteInfo && (
          <div style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--surface)', border: '1px solid var(--danger)',
            borderRadius: 'var(--radius)', padding: '12px 20px',
            color: 'var(--danger)', fontSize: 13, zIndex: 9999,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon name="x" size={14} /> {inviteError}
            <button onClick={() => setInviteError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 0, marginLeft: 4 }}>✕</button>
          </div>
        )}
      </>
    );
  }

  // ── Guest-mode UI (rendered in every non-auth view) ─────────────────────────
  //
  //  guestBanner  — fixed bottom bar prompting sign-in / sign-up.
  //  authPromptEl — modal shown when a guest tries a gated action.

  const guestBanner = (!user && guestMode) ? (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        fontSize: 13, fontFamily: 'var(--font-body)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
      }}
    >
      {pendingVerifyEmail ? (
        <>
          <span style={{ flex: 1, color: 'var(--text2)', minWidth: 180 }}>
            Check your inbox for <strong style={{ color: 'var(--text)' }}>{pendingVerifyEmail}</strong> to verify your account and unlock all features.
          </span>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '5px 14px' }}
            onClick={async () => {
              try { await Storage.resendVerification(pendingVerifyEmail); } catch {}
            }}
          >
            Resend Email
          </button>
          <button
            className="btn-ghost"
            style={{ fontSize: 12, padding: '5px 14px' }}
            onClick={handleLeaveGuest}
          >
            Sign In
          </button>
        </>
      ) : (
        <>
          <span style={{ flex: 1, color: 'var(--text2)', minWidth: 180 }}>
            You&apos;re browsing as a guest. Sign in or create a free account to interact.
          </span>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '5px 14px' }}
            onClick={handleLeaveGuest}
          >
            Sign In
          </button>
          <button
            className="btn-primary"
            style={{ fontSize: 12, padding: '5px 14px' }}
            onClick={handleLeaveGuest}
          >
            Create Account
          </button>
        </>
      )}
    </div>
  ) : null;

  const authPromptEl = authPrompt ? (
    <AuthPromptModal
      action={authPrompt.action}
      onSignIn={handleLeaveGuest}
      onSignUp={handleLeaveGuest}
      onClose={() => setAuthPrompt(null)}
    />
  ) : null;

  if (view === 'admin') {
    if (!user.isAdmin) { setView('dashboard'); return null; }
    return (
      <AdminPanel
        user={user}
        onBack={() => setView('dashboard')}
        theme={theme}
        onThemeToggle={handleThemeToggle}
      />
    );
  }

  if (view === 'profile') {
    return (
      <ProfilePage
        user={user}
        onSave={handleProfileSave}
        onBack={() => setView('dashboard')}
        onFeed={() => setView('feed')}
        onDeleteAccount={handleDeleteAccount}
        theme={theme}
        onThemeToggle={handleThemeToggle}
        canInstall={!!installPromptEvent && !isStandalone}
        isStandalone={isStandalone}
        onInstall={triggerInstall}
        onNotificationClick={handleNotificationClick}
        onViewProfile={handleViewProfile}
        onViewTimeline={(tl, author) => {
          setFeedViewAuthor(author);
          setViewerReturn('profile');
          if (tl.privacy === 'password-protected') {
            setPendingView(tl);
          } else {
            setActiveTimeline(tl);
            setView('viewer');
          }
        }}
      />
    );
  }

  if (view === 'editor' && activeTimeline) {
    const bannerDismissKey = `chronos_tpl_banner_${activeTimeline.id}`;
    const showTemplateBanner = !!fromTemplateName &&
      !localStorage.getItem(bannerDismissKey);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {showTemplateBanner && (
          <div className="tpl-editor-banner" role="status">
            <span className="tpl-editor-banner-text">
              You are editing a template. Replace the placeholder content with your own story.
              Look for <strong>[bracketed text]</strong> as prompts.
            </span>
            <button
              onClick={() => {
                localStorage.setItem(bannerDismissKey, '1');
                setFromTemplateName(null);
              }}
              aria-label="Dismiss banner"
            >✕</button>
          </div>
        )}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <TimelineEditor
            timeline={activeTimeline}
            onSave={handleSave}
            onBack={() => { setCollaboratorRole(null); setFromTemplateName(null); setView('dashboard'); }}
            theme={theme}
            onThemeToggle={handleThemeToggle}
            currentUser={user}
            collaboratorRole={collaboratorRole}
            onNotificationClick={handleNotificationClick}
            autoOpenCsvImport={pendingCsvImport}
            onAutoOpenCsvImportDone={() => setPendingCsvImport(false)}
          />
        </div>
      </div>
    );
  }

  if (view === 'viewer' && activeTimeline) {
    const sharedVersion = sharedTimelines.find(t => t.id === activeTimeline.id);
    // Guard against null user (guest mode).
    const isOwner   = user
      ? (activeTimeline.authorId
          ? activeTimeline.authorId === user.id
          : timelines.some(t => t.id === activeTimeline.id))
      : false;
    const isEditor  = activeTimeline.collaboratorRole === 'editor'
      || sharedVersion?.collaboratorRole === 'editor';
    const canEdit   = isOwner || isEditor;
    const live = isOwner
      ? (timelines.find(t => t.id === activeTimeline.id) || activeTimeline)
      : (sharedVersion || activeTimeline);
    const handleViewerBack = () => {
      setFeedViewAuthor(null);
      if (viewerReturn === 'publicProfile') {
        setView('publicProfile');
      } else if (viewerReturn === 'profile') {
        setView('profile');
      } else if (viewerReturn === 'feed') {
        setView('feed');
      } else if (!user) {
        setView('feed'); // guests go back to feed
      } else {
        setView('dashboard');
      }
    };
    return (
      <>
        <TimelineViewer
          timeline={live}
          onBack={handleViewerBack}
          backLabel={viewerReturn === 'feed' ? 'My Feed' : viewerReturn === 'publicProfile' ? 'Back' : viewerReturn === 'profile' ? 'My Profile' : 'My Dashboard'}
          onEdit={canEdit ? () => handleEdit(live) : null}
          onFork={(forked) => { setTimelines(prev => [forked, ...prev]); handleEdit(forked); }}
          currentUser={user}
          onViewProfile={handleViewProfile}
          onViewTimeline={handleFeedView}
          onAuthRequired={handleAuthRequired}
          theme={theme}
          onThemeToggle={handleThemeToggle}
          onNotificationClick={handleNotificationClick}
        />
        {guestBanner}
        {authPromptEl}
      </>
    );
  }

  if (view === 'publicProfile' && profileViewUser) {
    return (
      <>
        <PublicProfile
          userId={profileViewUser.id}
          currentUser={user}
          onBack={() => setView('feed')}
          onViewTimeline={handleProfileViewTimeline}
          onViewProfile={handleViewProfile}
          onNotificationClick={handleNotificationClick}
          theme={theme}
          onThemeToggle={handleThemeToggle}
          onAuthRequired={handleAuthRequired}
        />
        {guestBanner}
        {authPromptEl}
      </>
    );
  }

  if (view === 'feed') {
    return (
      <>
        <Feed
          user={user}
          onBack={user ? () => setView('dashboard') : null}
          onViewTimeline={handleFeedView}
          onNotificationClick={handleNotificationClick}
          onViewProfile={handleViewProfile}
          theme={theme}
          onThemeToggle={handleThemeToggle}
          onAuthRequired={handleAuthRequired}
          guestMode={!user && guestMode}
          onGuestSignIn={handleLeaveGuest}
          initialDevBlogPostId={initialDevBlogPostId}
          onDevBlogMounted={() => setInitialDevBlogPostId(null)}
        />
        {guestBanner}
        {authPromptEl}
        {/* Deep-link password gate — must be in this branch because Feed view is the
            landing state when a /timeline/:id URL is opened by an unauthenticated user. */}
        <Modal open={!!pendingView} onClose={closeGate} title="Private Timeline" width={400}>
          {pendingView && (
            pendingView.isPrivate ? (
              <div>
                <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
                  <strong style={{ color: 'var(--text)' }}>{pendingView.title}</strong> is private and can only be viewed by its owner.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-primary" onClick={closeGate}>OK</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
                  <strong style={{ color: 'var(--text)' }}>{pendingView.title}</strong> is password protected. Enter the password to view it.
                </p>
                <div style={{ marginBottom: gateError ? 8 : 20 }}>
                  <label htmlFor="gate-password-feed">Password</label>
                  <input
                    id="gate-password-feed"
                    type="password"
                    value={gatePassword}
                    onChange={e => { setGatePassword(e.target.value); setGateError(''); }}
                    placeholder="••••••••"
                    onKeyDown={e => e.key === 'Enter' && handleGate()}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                {gateError && (
                  <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                    {gateError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button className="btn-ghost" onClick={closeGate} disabled={gateLoading}>Cancel</button>
                  <button className="btn-primary" onClick={handleGate} disabled={gateLoading} style={{ opacity: gateLoading ? 0.7 : 1 }}>
                    {gateLoading ? 'Checking…' : 'View Timeline'}
                  </button>
                </div>
              </div>
            )
          )}
        </Modal>
      </>
    );
  }

  if (view !== 'dashboard') {
    // Unknown view state — should never happen, but guard against it.
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 24,
        fontFamily: 'var(--font-body)',
      }}>
        <div style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 20 }} aria-hidden="true">🔍</div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 28,
            color: 'var(--accent)',
            marginBottom: 12,
            letterSpacing: '-0.02em',
          }}>
            Page not found
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 15, lineHeight: 1.6, marginBottom: 32 }}>
            The page you're looking for doesn't exist or has been moved.
          </p>
          <button className="btn-primary" onClick={() => setView('dashboard')} style={{ minWidth: 160 }}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Onboarding welcome modal ── */}
      {showOnboarding && user && (
        <OnboardingModal
          user={user}
          onComplete={handleOnboardingComplete}
          onExplore={handleOnboardingExplore}
        />
      )}

      {/* ── Large-image upload warning toast ── */}
      {uploadWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '10px 16px',
          fontSize: 13, color: 'var(--text2)', zIndex: 10001,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          maxWidth: 420, textAlign: 'center',
          animation: 'fadeInUp 0.2s ease',
        }}>
          ⚡ {uploadWarning}
        </div>
      )}

      {/* ── Service worker update banner ── */}
      {(swUpdateAvailable || needRefresh) && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10001,
          background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
          padding: '10px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 13, fontFamily: 'var(--font-body)',
        }}>
          <span style={{ flex: 1, color: 'var(--text2)' }}>
            A new version of Chronos is available.
          </span>
          <button
            onClick={() => updateServiceWorker(true)}
            style={{
              background: 'var(--accent)', border: 'none', borderRadius: 6,
              color: '#1a1408', padding: '5px 14px', cursor: 'pointer',
              fontWeight: 600, fontSize: 12,
            }}
          >
            Update Now
          </button>
          <button
            onClick={() => setSwUpdateAvailable(false)}
            aria-label="Dismiss update banner"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Session expiry warning banner ── */}
      {showExpiryBanner && !expiryBannerDismissed && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          background: 'var(--accent)', color: '#1a1408',
          padding: '10px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 13, fontFamily: 'var(--font-body)',
        }}>
          <span style={{ flex: 1 }}>
            Your session expires soon. Sign in again to stay logged in.
          </span>
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 6,
              color: '#1a1408', padding: '5px 12px', cursor: 'pointer',
              fontWeight: 600, fontSize: 12,
            }}
          >
            Sign In Again
          </button>
          <button
            onClick={() => setExpiryBannerDismissed(true)}
            aria-label="Dismiss session expiry warning"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#1a1408', fontSize: 16, lineHeight: 1, padding: '2px 4px',
            }}
          >
            ✕
          </button>
        </div>
      )}

      <Dashboard
        user={user}
        timelines={timelines}
        sharedTimelines={sharedTimelines}
        onCreate={handleCreate}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onLogout={handleLogout}
        onProfile={() => setView('profile')}
        onFeed={() => setView('feed')}
        onAdmin={() => setView('admin')}
        onNotificationClick={handleNotificationClick}
        onViewProfile={handleViewProfile}
        theme={theme}
        onThemeToggle={handleThemeToggle}
        autoOpenCreate={pendingOpenCreate}
        onAutoOpenCreateDone={() => setPendingOpenCreate(false)}
        canInstall={!!installPromptEvent && !isStandalone}
        isStandalone={isStandalone}
        onInstall={triggerInstall}
      />

      {/* ── First-timeline celebration toast ── */}
      {showFirstTimeline && (
        <div className="first-tl-toast" role="status" aria-live="polite">
          <span>🎉 Your first timeline is live! Share it with the world.</span>
          <button
            className="btn-sm"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/timeline/${firstTimelineId}`).catch(() => {});
              setShowFirstTimeline(false);
            }}
          >
            Copy Link
          </button>
          <button
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, padding: '0 4px' }}
            onClick={() => setShowFirstTimeline(false)}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Password gate modal ── */}
      <Modal open={!!pendingView} onClose={closeGate} title="Private Timeline" width={400}>
        {pendingView && (
          pendingView.isPrivate ? (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
                <strong style={{ color: 'var(--text)' }}>{pendingView.title}</strong> is private and can only be viewed by its owner.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-primary" onClick={closeGate}>OK</button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20 }}>
                <strong style={{ color: 'var(--text)' }}>{pendingView.title}</strong> is password protected. Enter the password to view it.
              </p>
              <div style={{ marginBottom: gateError ? 8 : 20 }}>
                <label htmlFor="gate-password">Password</label>
                <input
                  id="gate-password"
                  type="password"
                  value={gatePassword}
                  onChange={e => { setGatePassword(e.target.value); setGateError(''); }}
                  placeholder="••••••••"
                  onKeyDown={e => e.key === 'Enter' && handleGate()}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {gateError && (
                <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                  {gateError}
                </p>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn-ghost" onClick={closeGate} disabled={gateLoading}>Cancel</button>
                <button className="btn-primary" onClick={handleGate} disabled={gateLoading} style={{ opacity: gateLoading ? 0.7 : 1 }}>
                  {gateLoading ? 'Checking…' : 'View Timeline'}
                </button>
              </div>
            </div>
          )
        )}
      </Modal>

      {/* ── Invite accept modal ── */}
      <Modal
        open={!!(inviteToken && (inviteInfo || inviteError)) }
        onClose={() => !inviteAccepting && !inviteDeclining && handleDeclineInvite()}
        title="Collaboration Invite"
        width={460}
      >
        {inviteError && !inviteInfo ? (
          <div>
            <p style={{ color: 'var(--danger)', marginBottom: 24 }}>{inviteError}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => { setInviteToken(null); setInviteError(''); }}>Close</button>
            </div>
          </div>
        ) : inviteInfo ? (
          <div>
            <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
                <strong style={{ color: 'var(--text)' }}>{inviteInfo.inviterName}</strong> has invited you to collaborate on:
              </p>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--accent)', marginBottom: 6 }}>
                {inviteInfo.timelineTitle}
              </p>
              <p style={{ fontSize: 12, color: 'var(--text3)' }}>
                Your role: <strong style={{
                  color: inviteInfo.role === 'editor' ? 'var(--accent)' : 'var(--text2)',
                }}>{inviteInfo.role === 'editor' ? 'Editor — can view and edit' : 'Viewer — can view only'}</strong>
              </p>
            </div>

            {inviteError && (
              <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{inviteError}</p>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn-ghost"
                onClick={handleDeclineInvite}
                disabled={inviteAccepting || inviteDeclining}
              >
                {inviteDeclining ? 'Declining…' : 'Decline'}
              </button>
              <button
                className="btn-primary"
                onClick={handleAcceptInvite}
                disabled={inviteAccepting || inviteDeclining}
                style={{ opacity: inviteAccepting ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name="userCheck" size={14} />
                {inviteAccepting ? 'Accepting…' : 'Accept Invite'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* ── Invite accepted confirmation ── */}
      <Modal open={inviteAccepted} onClose={dismissInviteAccepted} title="Invite Accepted!" width={400}>
        <p style={{ color: 'var(--text2)', marginBottom: 24, lineHeight: 1.6 }}>
          You now have access to the timeline. It will appear in your <strong style={{ color: 'var(--text)' }}>Shared With Me</strong> section on the dashboard.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={dismissInviteAccepted} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="users" size={14} /> Go to Dashboard
          </button>
        </div>
      </Modal>

      {/* ── Transfer processing spinner ── */}
      <Modal open={transferProcessing} onClose={() => {}} title="Processing Transfer…" width={360}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0 16px' }}>
          <div className="export-spinner" style={{ width: 20, height: 20, borderWidth: 2, flexShrink: 0 }} />
          <span style={{ color: 'var(--text2)', fontSize: 14 }}>Please wait…</span>
        </div>
      </Modal>

      {/* ── Auth prompt (guest / unverified-user gating) ── */}
      {authPromptEl}

      {/* ── Transfer result modal ── */}
      <Modal
        open={!!transferResult}
        onClose={() => { setTransferResult(null); setView('dashboard'); }}
        title={
          !transferResult ? '' :
          !transferResult.ok ? 'Transfer Link Error' :
          transferResult.action === 'accepted' ? 'Ownership Transferred!' : 'Transfer Declined'
        }
        width={440}
      >
        {transferResult && (
          <div>
            {!transferResult.ok ? (
              <>
                <p style={{ color: 'var(--danger)', marginBottom: 24, lineHeight: 1.6 }}>
                  {transferResult.error}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-ghost" onClick={() => { setTransferResult(null); }}>Close</button>
                </div>
              </>
            ) : transferResult.action === 'accepted' ? (
              <>
                <p style={{ color: 'var(--text2)', marginBottom: 8, lineHeight: 1.6 }}>
                  You are now the owner of:
                </p>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--accent)', marginBottom: 20 }}>
                  {transferResult.timelineTitle}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.6 }}>
                  The timeline has been added to your dashboard. The previous owner is now an editor.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn-primary"
                    onClick={() => { setTransferResult(null); setView('dashboard'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon name="layers" size={14} /> Open Dashboard
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--text2)', marginBottom: 24, lineHeight: 1.6 }}>
                  You have declined the ownership transfer. The timeline owner has been notified.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-ghost" onClick={() => { setTransferResult(null); }}>Close</button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default App;
